/****************
 * WHERE A NOTIFICATION OR AN OPEN TRADES TRAY ROW LANDS (Guillaume 11-02):
 * "when I click on it, it should re-direct me to where it is, it should be
 * about the middle of the page and glow." One link builder for every such
 * click, so none of them drifts to a page of its own again.
 *
 * Byte-identical twin in frontend/src/lib and mobile-app/src/lib.
 *   web:   /session/<id>?order=<orderId> | ?open=<SYMBOL> | ?trade=<tradeId>
 *   phone: /sessions/<id>?...             (the same three parameters)
 * The session page reads the parameter, opens the right tab, opens the list
 * far enough to hold the row (and the collapsed "not executed" orders when the
 * row is one of them), centres it and makes it glow for FOCUS_GLOW_MS.
 ****************/

export type FocusTarget =
  | { kind: 'order'; sessionId: number | string; orderId: number | string }
  | { kind: 'open'; sessionId: number | string; symbol: string }
  | { kind: 'trade'; sessionId: number | string; tradeId: number | string; why?: boolean }
  | { kind: 'session'; sessionId: number | string };

export type FocusApp = 'web' | 'phone';

/** The query parameters that ask a page to land on one row. */
export const FOCUS_PARAMS = ['order', 'open', 'trade'] as const;

/** How long the row glows once reached: a soft ring for about two seconds (11-03). */
export const FOCUS_GLOW_MS = 2000;

export function focusLink(target: FocusTarget, app: FocusApp): string {
  const base = `${app === 'web' ? '/session/' : '/sessions/'}${encodeURIComponent(String(target.sessionId))}`;
  switch (target.kind) {
    case 'order': return `${base}?order=${encodeURIComponent(String(target.orderId))}`;
    case 'open': return `${base}?open=${encodeURIComponent(target.symbol)}`;
    case 'trade': return `${base}?trade=${encodeURIComponent(String(target.tradeId))}${target.why ? '&why=1' : ''}`;
    default: return base;
  }
}

/** True when the address asks to land on one row (the page-change scroll to top then stands aside). */
export function hasFocusParam(search: string): boolean {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return FOCUS_PARAMS.some((p) => !!q.get(p));
}

/** The first-level section of a path: a change of section is a change of page. */
export function pageSection(path: string): string {
  const parts = path.replace(/^\/m(?=\/|$)/, '').split('/').filter(Boolean);
  const first = parts[0] ?? '';
  // The web's one session page and its list are both "My Copies".
  return first === 'session' ? 'sessions' : first;
}
